/**
 * Per-archetype massing recipes for the building grammar. Each recipe IS one
 * archetype read from the reference sheet — silhouette first (Tiny Glade
 * curation, not generality): habitation stacks shrinking slabs, watchtower is
 * a thin column, industrial a hall slab with raised edge strips, lab a
 * symmetric ziggurat with an occasional dome crown, marketplace a windowed
 * single-storey mat, outpost an armoured hull ring with an inset cap.
 *
 * IMPORT DISCIPLINE: plan.ts imports RECIPES (runtime) from this file, so this
 * file may import ONLY TYPES from plan.js — never runtime values — or the two
 * modules would form a runtime cycle.
 *
 * DETERMINISM: `massing`/`moduleFor` draw ONLY from the massing rng passed in
 * (some draw nothing at all — that is fine; the layer streams stay isolated).
 */
import type { Rng } from "../rng.js";
import type { BuildingArchetype, Massing } from "./plan.js";

/** One weighted greeble candidate for a socket class. `quotaGroup`, when set,
 * caps how many of that group land per building via `Recipe.quotas`. */
export interface GreebleEntry {
  greeble: string; socket: "roof" | "wall" | "ground";
  weight: number; quotaGroup?: string;
}

export interface Recipe {
  massing(rng: Rng, cellsW: number, cellsD: number, maxLevels: number): Massing;
  /** Module id for a tw×td tile. `topLevel` = index of the massing's top grid
   * (levels.length - 1), so crown modules (tower head) can key off it. */
  moduleFor(rng: Rng, wU: number, dU: number, level: number, topLevel: number): string;
  /** Greedy tiling footprints, largest-first, e.g. [[2,2],[2,1],[1,1]]. MUST
   * include the 1×1 tile: it is the fallback that guarantees every occupied cell
   * is covered, which buildingPlanHeightM's doorSide-invariance relies on. */
  tileSizes: [number, number][];
  /** Weighted greeble tables, keyed by socket class via `GreebleEntry.socket`. */
  greebles: GreebleEntry[];
  /** quotaGroup -> max instances per building (greebles without a group: no cap). */
  quotas: Record<string, number>;
  /** Per-socket-class fill probability (rolled per exposed socket). */
  fillProb: { roof: number; wall: number; ground: number };
  /** Weighted wildcard table — rolled at most once per building, p = 1/8. */
  wildcards: { greeble: string; weight: number }[];
}

/** Rasterize one axis-aligned rect per level into occupancy grids. Rects are
 * clamped to the grid; levels that end up empty are dropped so the plan's
 * `levels` count only ever reports REAL emitted levels. */
export function rectMassing(
  cellsW: number, cellsD: number,
  levels: { x0: number; z0: number; w: number; d: number }[],
): Massing {
  const grids: Uint8Array[] = [];
  for (const r of levels) {
    const g = new Uint8Array(cellsW * cellsD);
    const x1 = Math.min(cellsW, r.x0 + r.w);
    const z1 = Math.min(cellsD, r.z0 + r.d);
    let any = false;
    for (let gz = Math.max(0, r.z0); gz < z1; gz++) {
      for (let gx = Math.max(0, r.x0); gx < x1; gx++) {
        g[gz * cellsW + gx] = 1;
        any = true;
      }
    }
    if (!any) break; // empty level: nothing above it could be supported either
    grids.push(g);
  }
  return { cellsW, cellsD, levels: grids };
}

const habitation: Recipe = {
  tileSizes: [[2, 2], [2, 1], [1, 1]],
  greebles: [
    { greeble: "grb-tank-s", socket: "roof", weight: 3 },
    { greeble: "grb-vent", socket: "roof", weight: 3 },
    { greeble: "grb-antenna", socket: "roof", weight: 2 },
    { greeble: "grb-dish", socket: "roof", weight: 1, quotaGroup: "dish" },
    { greeble: "grb-awning", socket: "wall", weight: 2 },
    { greeble: "grb-banner", socket: "wall", weight: 1 },
    { greeble: "grb-crate", socket: "ground", weight: 3 },
  ],
  quotas: { dish: 1 },
  fillProb: { roof: 0.45, wall: 0.25, ground: 0.3 },
  wildcards: [
    { greeble: "grb-watertower", weight: 3 },
    { greeble: "grb-banner", weight: 1 },
  ],
  moduleFor: (rng, wU, dU) => (wU === 2 && dU === 1) ? "mod-hall"
    : (wU === 1 && dU === 1 && rng() < 0.6) ? "mod-box-win" : "mod-box",
  massing(rng, cellsW, cellsD, maxLevels) {
    // level 0 full; each next level a shrunk sub-rect offset inside the previous
    const rects = [{ x0: 0, z0: 0, w: cellsW, d: cellsD }];
    // NAVIGABILITY (2026-08-02 user call "we ALWAYS need a way to upper
    // floors"): a 1×1 storey can never host a stair with a valid arrival
    // (shellFor), so it becomes a SEALED room. A 1×1 lot stays a hut, and the
    // shrink ladder stops before emitting a 1×1 attic.
    const levels = cellsW === 1 && cellsD === 1
      ? 1
      : Math.min(maxLevels, 2 + Math.floor(rng() * 3)); // 2..4, capped
    for (let L = 1; L < levels; L++) {
      const prev = rects[L - 1];
      const w = Math.max(1, prev.w - (1 + Math.floor(rng() * 2)));
      const d = Math.max(1, prev.d - Math.floor(rng() * 2));
      if (w === prev.w && d === prev.d && L > 1) break;
      if (w === 1 && d === 1) break; // no sealed 1×1 attics
      rects.push({
        x0: prev.x0 + Math.floor(rng() * (prev.w - w + 1)),
        z0: prev.z0 + Math.floor(rng() * (prev.d - d + 1)), w, d,
      });
    }
    return rectMassing(cellsW, cellsD, rects);
  },
};

/** Thin column at the grid CENTRE — a watchtower on a big footprint is still a
 * 1×1 spire, never a slab. Top level is the overhanging head; the column base
 * is the door cell, which tileMassing swaps to `mod-box-door` (phase-1 read).
 * Draws nothing from the rng: the silhouette is fully input-determined. */
const watchtower: Recipe = {
  tileSizes: [[1, 1]],
  greebles: [
    { greeble: "grb-antenna", socket: "roof", weight: 4 },
    { greeble: "grb-dish", socket: "roof", weight: 2, quotaGroup: "dish" },
  ],
  quotas: { dish: 2 },
  fillProb: { roof: 0.6, wall: 0.1, ground: 0.15 },
  wildcards: [{ greeble: "grb-banner", weight: 1 }],
  moduleFor: (_rng, _wU, _dU, level, topLevel) =>
    level === topLevel ? "mod-tower-head" : "mod-tower-shaft",
  massing(_rng, cellsW, cellsD, maxLevels) {
    const x0 = (cellsW - 1) >> 1, z0 = (cellsD - 1) >> 1; // centre cell
    // Always ≥ 2: towers overshoot small caps (tall read). Landmark budgets
    // (garrisons, 2026-08-02) climb the whole budget up to 10 shafts.
    const n = maxLevels <= 5 ? Math.min(maxLevels + 1, 6) : Math.min(maxLevels, 10);
    const rects = [];
    for (let L = 0; L < n; L++) rects.push({ x0, z0, w: 1, d: 1 });
    return rectMassing(cellsW, cellsD, rects);
  },
};

/** Hall slab with 1–2 raised strips along one edge — the long-roof factory
 * read. Level 1 is built directly (two strips can't be one rect). */
const industrial: Recipe = {
  tileSizes: [[2, 1], [1, 1]],
  greebles: [
    { greeble: "grb-tank-l", socket: "roof", weight: 4, quotaGroup: "bigtank" },
    { greeble: "grb-vent", socket: "roof", weight: 4 },
    { greeble: "grb-crate", socket: "ground", weight: 4 },
  ],
  quotas: { bigtank: 2 },
  fillProb: { roof: 0.5, wall: 0.1, ground: 0.35 },
  wildcards: [{ greeble: "grb-tank-l", weight: 1 }], // a third, giant read
  moduleFor: (_rng, wU, dU) => (wU === 2 && dU === 1) ? "mod-hall" : "mod-box",
  massing(rng, cellsW, cellsD, maxLevels) {
    const base = new Uint8Array(cellsW * cellsD).fill(1);
    const levels = [base];
    // ≥2 total cells: a 1×1 grid's "strip" is a sealed 1×1 upper room
    // (2026-08-02 navigability rule — see habitation).
    if (maxLevels >= 2 && cellsW * cellsD >= 2) {
      const g = new Uint8Array(cellsW * cellsD);
      const edge = Math.floor(rng() * 4);   // 0=+z 1=−z 2=+x 3=−x
      const second = rng() < 0.5;           // second strip, one row further in
      const strip = (inset: number): void => {
        if (edge <= 1) {
          const gz = edge === 1 ? inset : cellsD - 1 - inset;
          for (let gx = 0; gx < cellsW; gx++) g[gz * cellsW + gx] = 1;
        } else {
          const gx = edge === 3 ? inset : cellsW - 1 - inset;
          for (let gz = 0; gz < cellsD; gz++) g[gz * cellsW + gx] = 1;
        }
      };
      strip(0);
      // only widen when ≥ 3 rows deep so some hall roof always stays exposed
      if (second && (edge <= 1 ? cellsD : cellsW) >= 3) strip(1);
      levels.push(g);
    }
    return { cellsW, cellsD, levels };
  },
};

/** Symmetric ziggurat: upper rects shrink by one cell PER SIDE and stay
 * centred; 35% of seeds (massing-stream roll) crown the top with `mod-dome`
 * via `Massing.domeCell` — the top-level occupied cell nearest the footprint
 * centre (the largest exposed read), emitted by tileMassing one level up. */
const lab: Recipe = {
  tileSizes: [[2, 1], [1, 1]],
  greebles: [
    { greeble: "grb-dish", socket: "roof", weight: 3, quotaGroup: "dish" },
    { greeble: "grb-antenna", socket: "roof", weight: 3 },
    { greeble: "grb-crate", socket: "ground", weight: 1 },
  ],
  quotas: { dish: 2 },
  fillProb: { roof: 0.45, wall: 0.15, ground: 0.15 },
  wildcards: [], // labs stay austere: no wildcard table (roll still consumed)
  moduleFor: (rng, wU, dU) => (wU === 2 && dU === 1) ? "mod-hall"
    : rng() < 0.8 ? "mod-box-win" : "mod-box",
  massing(rng, cellsW, cellsD, maxLevels) {
    const rects = [{ x0: 0, z0: 0, w: cellsW, d: cellsD }];
    // 2..3 normally; a LANDMARK budget (2026-08-02 civic wave, maxLevels > 5)
    // keeps climbing — the ziggurat narrows then rises as a ≥2-cell shaft, so
    // temples/basilicas/academies read as real weenies, not flattened slabs.
    // NAVIGABILITY: upper storeys floor at 2 cells (a 1×1 storey can't host a
    // stair with a valid arrival → sealed room), and a 1-cell-thin lot never
    // stacks at all.
    const levels = Math.min(cellsW, cellsD) === 1
      ? 1
      : Math.min(maxLevels, 2 + Math.floor(rng() * 2) + Math.max(0, maxLevels - 5));
    for (let L = 1; L < levels; L++) {
      const prev = rects[L - 1];
      const w = Math.max(Math.min(2, cellsW), prev.w - 2);
      const d = Math.max(Math.min(2, cellsD), prev.d - 2);
      rects.push({
        x0: prev.x0 + ((prev.w - w) >> 1),
        z0: prev.z0 + ((prev.d - d) >> 1), w, d,
      });
    }
    const m = rectMassing(cellsW, cellsD, rects);
    if (rng() < 0.35) {
      const top = m.levels[m.levels.length - 1];
      let best = -1, bestDist = 0;
      for (let gz = 0; gz < cellsD; gz++) for (let gx = 0; gx < cellsW; gx++) {
        if (top[gz * cellsW + gx] !== 1) continue;
        const ddx = 2 * gx - (cellsW - 1), ddz = 2 * gz - (cellsD - 1);
        const dist = ddx * ddx + ddz * ddz;
        if (best === -1 || dist < bestDist) { best = gz * cellsW + gx; bestDist = dist; }
      }
      if (best >= 0) m.domeCell = [best % cellsW, (best - (best % cellsW)) / cellsW];
    }
    return m;
  },
};

/** Single-storey windowed mat — every tile is `mod-box-win` (the door tile is
 * reserved by tileMassing before moduleFor is consulted). `levels` is 1 for
 * ALL footprints; the standard entry rule then applies unchanged. */
const marketplace: Recipe = {
  tileSizes: [[1, 1]],
  greebles: [
    { greeble: "grb-awning", socket: "wall", weight: 5 },
    { greeble: "grb-crate", socket: "ground", weight: 4 },
    { greeble: "grb-banner", socket: "wall", weight: 2 },
  ],
  quotas: {},
  fillProb: { roof: 0, wall: 0.6, ground: 0.7 }, // stalls, not rooftop clutter
  wildcards: [{ greeble: "grb-banner", weight: 1 }],
  moduleFor: () => "mod-box-win",
  massing: (_rng, cellsW, cellsD, maxLevels) => {
    const rects = [{ x0: 0, z0: 0, w: cellsW, d: cellsD }];
    // Ordinary markets stay the single-storey mat. A GRAND market hall
    // (landmark budget, 2026-08-02) raises a centred clerestory hall over it.
    if (maxLevels >= 6 && cellsW >= 3 && cellsD >= 3) {
      const upper = Math.min(maxLevels - 4, 4);
      const w = Math.max(2, cellsW - 2), d = Math.max(2, cellsD - 2);
      for (let L = 0; L < upper; L++) {
        rects.push({ x0: (cellsW - w) >> 1, z0: (cellsD - d) >> 1, w, d });
      }
    }
    return rectMassing(cellsW, cellsD, rects);
  },
};

/** Armoured ring: level 0 all tapered `mod-hull`, level 1 an inset-by-1 plain
 * cap. Never windows — the fortified read stays blind. On footprints too small
 * to inset (≤ 2 cells on either axis) rectMassing drops the empty cap level. */
const outpost: Recipe = {
  tileSizes: [[1, 1]],
  greebles: [
    { greeble: "grb-banner", socket: "wall", weight: 3 },
    { greeble: "grb-antenna", socket: "roof", weight: 2 },
    { greeble: "grb-crate", socket: "ground", weight: 2 },
  ],
  quotas: {},
  fillProb: { roof: 0.3, wall: 0.25, ground: 0.25 },
  wildcards: [{ greeble: "grb-watertower", weight: 1 }],
  moduleFor: (_rng, _wU, _dU, level) => level === 0 ? "mod-hull" : "mod-box",
  massing: (_rng, cellsW, cellsD, maxLevels) => {
    const rects = [{ x0: 0, z0: 0, w: cellsW, d: cellsD }];
    if (maxLevels >= 2) {
      // Inset cap; a LANDMARK budget (palaces/guildhalls, 2026-08-02) raises
      // the inset as a straight-walled KEEP climbing most of the budget.
      const keepLevels = Math.min(Math.max(1, maxLevels - 1), 1 + Math.max(0, maxLevels - 4));
      for (let L = 0; L < keepLevels; L++) {
        rects.push({ x0: 1, z0: 1, w: cellsW - 2, d: cellsD - 2 });
      }
    }
    return rectMassing(cellsW, cellsD, rects);
  },
};

export const RECIPES: Record<BuildingArchetype, Recipe> = {
  habitation, watchtower, industrial, lab, marketplace, outpost,
};
